import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Form, Input, Spin } from 'antd';
import { connect } from 'react-redux';
import { confirmEmail as confirmEmailAction,
  confirmClear as confirmClearAction,
  confirmErrorClear as confirmErrorClearAction,
  ConfirmResponse } from '@triare/auth-redux/dist/saga/auth/confirm';

import { resetPassword as resetPasswordAction,
  resetPasswordClear as resetPasswordClearAction,
  resetPasswordErrorClear as resetPasswordErrorClearAction } from '@triare/auth-redux/dist/saga/auth/resetPassword';
import { Error } from '@triare/auth-redux/dist/saga/common';

import { LoadingOutlined } from '@ant-design/icons';
import Wrapper from '../Wrapper';
import { getMessageInError } from '../../../../hooks/fetch';
import { createRulesForAntd, validationNewPassword } from '../../../../utils/validations';
import Submit from '../../../Common/Form/Button/Submit';
import { moduleName } from '../../../../store/auth';
import { RootState } from '../../../../store';

const validationRules = createRulesForAntd(validationNewPassword);

export interface ConfirmEmailProps {
  mode?: string;
  loading: boolean;
  response: ConfirmResponse | null;
  error: Error | null;
  confirmEmail: typeof confirmEmailAction;
  confirmClear: typeof confirmErrorClearAction;
  confirmErrorClear: typeof confirmErrorClearAction;
  resetPasswordLoading: boolean;
  resetPasswordResponse: ConfirmResponse | null;
  resetPasswordError: Error | null;
  resetPassword: typeof resetPasswordAction;
  resetPasswordClear: typeof resetPasswordClearAction;
  resetPasswordErrorClear: typeof resetPasswordErrorClearAction;
}

function ConfirmEmail({
  mode: modeParam = undefined,
  response,
  loading,
  error,
  confirmEmail,
  confirmClear,
  confirmErrorClear,
  resetPasswordLoading,
  resetPasswordResponse,
  resetPasswordError,
  resetPassword,
  resetPasswordClear,
  resetPasswordErrorClear,
}: ConfirmEmailProps): JSX.Element {
  const { mode = modeParam, secretKey } = useParams();
  const navigate = useNavigate();
  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    if (secretKey && mode === 'none') {
      confirmEmail({
        secretKey,
      });
    }
  }, [secretKey]);

  useEffect(() => {
    if (!loading && !error) {
      if (response) {
        setRedirect(true);
      } else if (redirect) {
        navigate('/');
      }
    }
    if (!resetPasswordLoading && !resetPasswordError) {
      if (resetPasswordResponse) {
        setRedirect(true);
      } else if (redirect) {
        navigate('/');
      }
    }
  }, [response, error, loading, resetPasswordResponse, resetPasswordError, resetPasswordLoading, redirect]);

  if (mode === 'password') {
    return (
      <Wrapper>
        <h1 style={{ textAlign: 'center' }}>Email confirmation!</h1>

        <div style={{ padding: '16px 32px', textAlign: 'center' }}>
          In order to complete your sign-up process, please enter your password.
        </div>

        <Form
          layout="vertical"
          onFinish={({ password }) => {
            resetPassword({
              secretKey: secretKey || '',
              password,
            });
          }}
        >
          <Form.Item name="password" label="Password *" rules={[validationRules]}>
            <Input.Password placeholder="Password" size="large" />
          </Form.Item>

          {resetPasswordResponse && !resetPasswordError ? (
            <Alert
              type="success"
              message="Signed in successfully!"
              style={{ margin: '8px 0 24px' }}
              closable
              onClose={resetPasswordClear}
            />
          ) : null}

          {error ? (
            <Alert
              style={{ margin: '8px 0 24px' }}
              type="error"
              message={getMessageInError(error)}
              closable
              onClose={resetPasswordErrorClear}
            />
          ) : null}

          <Submit size="large" loading={resetPasswordLoading}>
            Confirm
          </Submit>
        </Form>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <h1 style={{ textAlign: 'center' }}>
        Your email confirm
        {error ? ' ' : null}
        {(response || redirect) && !error ? ' successfully!' : <Spin indicator={<LoadingOutlined spin />} />}
      </h1>

      {response && !error ? (
        <Alert
          type="success"
          message="Signed in successfully!"
          style={{ margin: '8px 0 24px' }}
          closable
          onClose={confirmClear}
        />
      ) : null}

      {error ? (
        <Alert
          style={{ marginTop: 8 }}
          type="error"
          message="Link is expired"
          closable
          onClose={confirmErrorClear}
        />
      ) : null}
    </Wrapper>
  );
}

export default connect(
  (state: RootState) => ({
    loading: state[moduleName].confirm.loading,
    response: state[moduleName].confirm.response,
    error: state[moduleName].confirm.error,
    resetPasswordLoading: state[moduleName].resetPassword.loading,
    resetPasswordResponse: state[moduleName].resetPassword.response,
    resetPasswordError: state[moduleName].resetPassword.error,
  }),
  {
    confirmEmail: confirmEmailAction,
    confirmClear: confirmClearAction,
    confirmErrorClear: confirmErrorClearAction,
    resetPassword: resetPasswordAction,
    resetPasswordClear: resetPasswordClearAction,
    resetPasswordErrorClear: resetPasswordErrorClearAction,
  },
)(ConfirmEmail);
